import React from 'react'
import styled from 'styled-components'
import {ArrowUp, ArrowDown, CircleAlert, Info} from 'lucide-react'
import {Skeleton} from '../BaseComponents'

/**
 * KpiCard
 *
 * The canonical dashboard KPI card — an uppercase label, a tinted icon badge, a
 * large value, and an optional footer (delta chip + supporting text). When the
 * value is missing (null/undefined), it renders an em-dash + an `emptyNote`
 * "awaiting data" line instead. Role-agnostic; reusable across every dashboard's
 * KPI strip (order summary, financial, agency performance, etc.).
 *
 * Per-card states, for a strip that keeps its place while figures load or when
 * one figure fails and the rest stand: `loading` (a busy skeleton, no footer) and
 * `error` (an em-dash + an error line, never the grey empty note, so a failure
 * cannot read as "no data"). Precedence matches DashboardCard: loading, then
 * error, then empty. Omit both and the card renders exactly as before they existed.
 *
 * `action` (STFFLG-4230) puts a control at the card's foot, e.g. the MSP dashboard's full-width
 * "Select branches" button in its Selected branches card. It renders in every state, loading and
 * error included, so the control never vanishes while the figure refetches. A card with an action
 * has no hover lift: the card is no longer one clickable thing, and a lift would move the anchor
 * of a panel the action opens. Omit it and the card renders exactly as before it existed.
 *
 * @example
 *   <KpiCard label="Filled this month" value={31} tone="success"
 *     icon={<CircleCheck size={18} />} delta={{value: '+8', direction: 'up'}}
 *     footText="vs last month" />
 *   <KpiCard label="Fill rate" value={null} tone="success"
 *     icon={<Target size={18} />} emptyNote="Waiting for WorkerFillEvent data" />
 *   <KpiCard label="Fill rate" loading={isLoading} error={Boolean(error)}
 *     value={data?.fillRate ?? null} />
 *   <KpiCard label="Selected branches" value={3} footText="of 10 branches"
 *     action={<BranchSelectDropdown … />} />
 */

export type KpiTone = 'brand' | 'navy' | 'success' | 'info' | 'pending' | 'warning'

export interface KpiDelta {
  value: string | number
  direction: 'up' | 'down' | 'neutral'
}

export interface KpiCardProps {
  label: string
  /** The metric value; `null`/`undefined` renders the empty "awaiting data" state. */
  value?: string | number | null
  /** Leading icon (e.g. a lucide icon element), shown in the tinted badge. */
  icon?: React.ReactNode
  /** Icon-badge color (default `navy`). */
  tone?: KpiTone
  /** Render the value in the mono font (currency / long figures). */
  money?: boolean
  /** Render the value in the mono font (non-currency numerics like `14m`, `3,420`). */
  mono?: boolean
  /** Optional delta chip (e.g. `{value: '+8', direction: 'up'}`). Ignored when empty. */
  delta?: KpiDelta
  /** Optional supporting text in the footer. Ignored when empty. */
  footText?: string
  /** Footer note shown (with an info icon) when the value is empty/awaiting data. */
  emptyNote?: string
  /**
   * The value is not known yet: a skeleton stands in for it, no footer shows (`action` still does),
   * and the card is marked `aria-busy`. Wins over `error` and the empty state.
   */
  loading?: boolean
  /**
   * The value could not be read (truthy = error): an em-dash and `errorLabel` in error text, with
   * an icon named "Error". Delta, footer text and `emptyNote` are withheld. Wins over the empty
   * state. The same type as DashboardCard's `error`.
   */
  error?: Error | null | boolean
  /** The error line's text (default "Couldn’t load"). */
  errorLabel?: string
  /**
   * A control at the foot of the card, below the footer, in every state (loading, error and empty
   * too). Its wrapper takes the test id `${data-testid}-action`. Setting it turns the card's hover
   * lift off.
   */
  action?: React.ReactNode
  'data-testid'?: string
}

const TONE: Record<KpiTone, {bg: string; fg: string}> = {
  brand: {bg: 'var(--brand-100)', fg: 'var(--brand-cta)'},
  navy: {bg: 'var(--primary-100)', fg: 'var(--primary-800)'},
  success: {bg: 'var(--success-100)', fg: 'var(--success-800)'},
  info: {bg: 'var(--info-100)', fg: 'var(--info-800)'},
  pending: {bg: 'var(--pending-100)', fg: 'var(--pending-800)'},
  warning: {bg: 'var(--warning-100)', fg: 'var(--warning-800)'},
}

const DELTA: Record<KpiDelta['direction'], {bg: string; fg: string}> = {
  up: {bg: 'var(--success-100)', fg: 'var(--success-800)'},
  down: {bg: 'var(--error-100)', fg: 'var(--error-800)'},
  neutral: {bg: 'var(--neutral-200)', fg: 'var(--neutral-700)'},
}

const Card = styled.div<{$lift: boolean}>`
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-regular);
  box-sizing: border-box;
  /* Fill the grid cell so KPI cards sharing a row are equal height even when
     one of them has a footer line (delta chip / "56 branches") and the others
     do not — matches DashboardCard / OrderAgingCard, which already do this. */
  height: 100%;
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  transition: transform 0.2s, box-shadow 0.2s;

  /* The hover lift. Off with an action (STFFLG-4230): the card is not one control then, and the
     rise would move the anchor of the panel the action opens. The -2px is the lift every card
     already had, moved here unchanged so existing cards keep their exact hover (not a new size). */
  ${({$lift}) =>
    $lift &&
    `
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-elevated);
    }
  `}
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
`

const Label = styled.span`
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const IconWrap = styled.span<{$tone: KpiTone}>`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({$tone}) => TONE[$tone].bg};
  color: ${({$tone}) => TONE[$tone].fg};
`

const Value = styled.div<{$money: boolean; $mono: boolean; $empty: boolean}>`
  font-weight: var(--fw-bold);
  color: var(--primary-800);
  line-height: 1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.03em;
  font-size: 2.25rem;

  ${({$money}) =>
    $money &&
    `
    font-family: var(--font-mono);
    font-size: var(--fs-h1);
    font-weight: var(--fw-semibold);
    letter-spacing: -0.02em;
  `}
  ${({$mono}) =>
    $mono &&
    `
    font-family: var(--font-mono);
    font-size: 1.875rem;
    font-weight: var(--fw-semibold);
    letter-spacing: -0.02em;
  `}
  ${({$empty}) =>
    $empty &&
    `
    font-family: var(--font-mono);
    opacity: 0.45;
  `}
`

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
  font-size: var(--fs-caption);
  color: var(--text-secondary);
`

const EmptyNote = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--fs-caption);
  color: var(--text-secondary);

  svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }
`

/** The empty note's layout in error text (small status text is `--*-800`, §3a). */
const ErrorNote = styled(EmptyNote)`
  color: var(--error-800);
`

const DeltaChip = styled.span<{$dir: KpiDelta['direction']}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-pill);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  background: ${({$dir}) => DELTA[$dir].bg};
  color: ${({$dir}) => DELTA[$dir].fg};

  svg {
    width: 12px;
    height: 12px;
  }
`

/**
 * The `action` slot. `margin-top: auto` takes the spare height of a card its row stretched, so the
 * control sits at the card's foot and lines up across the strip.
 */
const Action = styled.div`
  margin-top: auto;
`

export function KpiCard({
  label,
  value,
  icon,
  tone = 'navy',
  money = false,
  mono = false,
  delta,
  footText,
  emptyNote,
  loading = false,
  error,
  errorLabel = 'Couldn’t load',
  action,
  'data-testid': dataTestId,
}: KpiCardProps) {
  const isEmpty = value === null || value === undefined || value === ''
  const failed = !loading && Boolean(error)

  return (
    <Card $lift={!action} data-testid={dataTestId} aria-busy={loading || undefined}>
      <Head>
        <Label>{label}</Label>
        {icon ? <IconWrap $tone={tone}>{icon}</IconWrap> : null}
      </Head>
      {loading ? (
        <Value $money={money} $mono={mono} $empty={false}>
          {/* 1em of the value's own font size: the skeleton takes the value's line box. */}
          <Skeleton width="60%" height="1em" />
        </Value>
      ) : (
        <Value $money={money} $mono={mono} $empty={isEmpty || failed}>
          {isEmpty || failed ? '—' : value}
        </Value>
      )}
      {loading ? null : failed ? (
        <ErrorNote>
          <CircleAlert role="img" aria-label="Error" />
          {errorLabel}
        </ErrorNote>
      ) : isEmpty ? (
        emptyNote ? (
          <EmptyNote>
            <Info />
            {emptyNote}
          </EmptyNote>
        ) : null
      ) : delta || footText ? (
        <Foot>
          {delta ? (
            <DeltaChip $dir={delta.direction}>
              {delta.direction === 'up' ? (
                <ArrowUp />
              ) : delta.direction === 'down' ? (
                <ArrowDown />
              ) : null}
              {delta.value}
            </DeltaChip>
          ) : null}
          {footText ? <span>{footText}</span> : null}
        </Foot>
      ) : null}
      {action ? (
        <Action data-testid={dataTestId ? `${dataTestId}-action` : undefined}>{action}</Action>
      ) : null}
    </Card>
  )
}
